import React, { useEffect } from 'react';
import { Box, IconButton, Portal } from '@mui/material';
import { X } from 'lucide-react';

interface SlidePanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}

const SlidePanel = ({ open, onClose, children, width = 600 }: SlidePanelProps) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  return (
    <Portal>
      {/* Container for both backdrop and panel */}
      <Box 
        sx={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          pointerEvents: open ? 'auto' : 'none'
        }}
      >
        {/* Backdrop */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            opacity: open ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
          onClick={onClose}
        />

        {/* Panel */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: width,
            backgroundColor: 'background.paper',
            boxShadow: theme => theme.shadows[24],
            transform: open ? 'translateX(0)' : `translateX(${width}px)`,
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <X size={20} />
          </IconButton>

          {/* Content */}
          <Box 
            sx={{ 
              flex: 1, 
              p: 3, 
              pt: 6,
              height: '100%',
              overflow: 'auto'
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Portal>
  );
};

export default SlidePanel;